import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SeoTags } from "../../components/Head/SeoTags";
import { ExtensionMock } from "../../components/Mockups/ExtensionMock";

const Extensions = () => {
  const { t } = useTranslation();

  const browsers = [
    {
      name: "Chrome",
      icon: "bi-browser-chrome",
      color: "success",
      status: "available",
      users: null,
      rating: null,
      downloadUrl:
        "https://chromewebstore.google.com/detail/onbookmarks/mkegjpfabinfdfgiemmfhpfmfeokdbpo",
      features: [
        t("extensions.browser.features.oneClick"),
        t("extensions.browser.features.aiSuggestions"),
        t("extensions.browser.features.instantSync"),
        t("extensions.browser.features.keyboardShortcuts"),
      ],
    },
    {
      name: "Firefox",
      icon: "bi-browser-firefox",
      color: "orange",
      status: "available",
      users: null,
      rating: null,
      downloadUrl: "https://addons.mozilla.org/en-US/firefox/addon/onbookmarks/",
      features: [
        t("extensions.browser.features.oneClick"),
        t("extensions.browser.features.aiSuggestions"),
        t("extensions.browser.features.instantSync"),
        t("extensions.browser.features.privacyFirst"),
      ],
    },
    {
      name: "Edge",
      icon: "bi-browser-edge",
      color: "info",
      status: "available",
      users: null,
      rating: null,
      downloadUrl:
        "https://microsoftedge.microsoft.com/addons/detail/gccckckcmaolhemnamcaigcbmaebnodc",
      features: [
        t("extensions.browser.features.oneClick"),
        t("extensions.browser.features.aiSuggestions"),
        t("extensions.browser.features.instantSync"),
        t("extensions.browser.features.microsoftIntegration"),
      ],
    },
    {
      name: "Safari",
      icon: "bi-browser-safari",
      color: "primary",
      status: "coming-q3",
      users: null,
      rating: null,
      downloadUrl: null,
      features: [
        t("extensions.browser.features.oneClick"),
        t("extensions.browser.features.aiSuggestions"),
        t("extensions.browser.features.instantSync"),
        t("extensions.browser.features.macosIntegration"),
      ],
    },
  ];

  const extensionFeatures = [
    {
      icon: "bi-lightning-charge",
      title: t("extensions.extensionFeatures.features.lightningFast.title"),
      description: t(
        "extensions.extensionFeatures.features.lightningFast.description"
      ),
    },
    {
      icon: "bi-robot",
      title: t("extensions.extensionFeatures.features.aiPowered.title"),
      description: t(
        "extensions.extensionFeatures.features.aiPowered.description"
      ),
    },
    {
      icon: "bi-arrow-repeat",
      title: t("extensions.extensionFeatures.features.realTimeSync.title"),
      description: t(
        "extensions.extensionFeatures.features.realTimeSync.description"
      ),
    },
    {
      icon: "bi-clipboard-check",
      title: t("extensions.extensionFeatures.features.smartClipboard.title"),
      description: t(
        "extensions.extensionFeatures.features.smartClipboard.description"
      ),
    },
    {
      icon: "bi-shield-check",
      title: t("extensions.extensionFeatures.features.privacyFirst.title"),
      description: t(
        "extensions.extensionFeatures.features.privacyFirst.description"
      ),
    },
    {
      icon: "bi-gear",
      title: t("extensions.extensionFeatures.features.customizable.title"),
      description: t(
        "extensions.extensionFeatures.features.customizable.description"
      ),
    },
  ];

  // Effect to scroll to the download section if the URL contains a hash
  useEffect(() => {
    const hash = window.location.hash;
    if (hash === "#download") {
      const downloadSection = document.getElementById("download");
      if (downloadSection) {
        downloadSection.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, []);

  return (
    <>
      <SeoTags
        title={t("extensions.seo.title")}
        description={t("extensions.seo.description")}
        keywords={t("extensions.seo.keywords")}
      />

      {/* SoftwareApplication Schema for Browser Extensions */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name:
              t("extensions.hero.title") +
              " " +
              t("extensions.hero.titleHighlight"),
            applicationCategory: "BrowserApplication",
            operatingSystem: "Chrome, Firefox, Safari, Edge",
            offers: {
              "@type": "Offer",
              price: "0",
              priceCurrency: "USD",
            },
            downloadUrl:
              "https://chromewebstore.google.com/detail/onbookmarks/mkegjpfabinfdfgiemmfhpfmfeokdbpo",
            description: t("extensions.seo.description"),
            features: extensionFeatures.map((feature) => feature.title),
            publisher: {
              "@type": "Organization",
              name: "onBookmarks",
            },
          }),
        }}
      />

      {/* WebPage Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: t("extensions.seo.title"),
            description: t("extensions.seo.description"),
            url: "https://onbookmarks.com/download/extensions",
            isPartOf: {
              "@type": "WebSite",
              name: "onBookmarks",
              url: "https://onbookmarks.com",
            },
            mainEntity: {
              "@type": "SoftwareApplication",
              name:
                t("extensions.hero.title") +
                " " +
                t("extensions.hero.titleHighlight"),
            },
          }),
        }}
      />

      {/* Breadcrumb Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://onbookmarks.com",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Download",
                item: "https://onbookmarks.com/download",
              },
              {
                "@type": "ListItem",
                position: 3,
                name: "Extensions",
                item: "https://onbookmarks.com/download/extensions",
              },
            ],
          }),
        }}
      />

      {/* HowTo Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            name: t("extensions.howItWorks.title"),
            description: t("extensions.howItWorks.title"),
            step: [
              {
                "@type": "HowToStep",
                name: t("extensions.howItWorks.steps.step1.title"),
                text: t("extensions.howItWorks.steps.step1.description"),
              },
              {
                "@type": "HowToStep",
                name: t("extensions.howItWorks.steps.step2.title"),
                text: t("extensions.howItWorks.steps.step2.description"),
              },
              {
                "@type": "HowToStep",
                name: t("extensions.howItWorks.steps.step3.title"),
                text: t("extensions.howItWorks.steps.step3.description"),
              },
            ],
            totalTime: "PT2M",
          }),
        }}
      />

      {/* Hero Section */}
      <section className="hero bg-light">
        <div className="container">
          <div className="row align-items-center min-vh-50">
            <div className="col-lg-6">
              <div className="d-flex flex-column gap-4">
                <div>
                  <span className="bg-success-subtle text-success px-3 py-2 rounded-3 fw-semibold">
                    <i className="bi bi-puzzle me-2 fs-5"></i>
                    {t("extensions.hero.badge")}
                  </span>
                </div>
                <h1 className="display-4 fw-bold">
                  {t("extensions.hero.title")}
                  <span className="text-primary-gradient d-block">
                    {t("extensions.hero.titleHighlight")}
                  </span>
                </h1>
                <p className="lead">{t("extensions.hero.description")}</p>
                <div className="d-flex gap-3 flex-wrap mb-4 mb-lg-0">
                  <a href="#download" className="btn btn-primary btn-lg px-4">
                    <i className="bi bi-download me-2"></i>
                    {t("extensions.hero.buttons.download")}
                  </a>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="position-relative d-flex justify-content-center">
                <ExtensionMock />
                {/* Floating browser icons */}
                <div className="position-absolute top-0 start-0 m-3">
                  <div className="bg-white icon shadow p-3">
                    <i className="bi bi-browser-chrome fs-4 text-success"></i>
                  </div>
                </div>
                <div className="position-absolute top-0 end-0 m-3">
                  <div className="bg-white icon shadow p-3">
                    <i className="bi bi-browser-firefox fs-4 text-orange"></i>
                  </div>
                </div>
                <div className="position-absolute bottom-0 start-0 m-3">
                  <div className="bg-white icon shadow p-3">
                    <i className="bi bi-browser-edge fs-4 text-info"></i>
                  </div>
                </div>
                <div className="position-absolute bottom-0 end-0 m-3">
                  <div className="bg-white icon shadow p-3">
                    <i className="bi bi-browser-safari fs-4 text-primary"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="download" className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="display-6 fw-bold mb-4">
              {t("extensions.availability.title")}
            </h2>
            <p className="lead text-muted">
              {t("extensions.availability.subtitle")}
            </p>
          </div>

          <div className="row g-4">
            {browsers.map((browser) => (
              <div key={browser.name} className="col-lg-3 col-md-6">
                <div className={`card h-100 border shadow-sm`}>
                  <div className="card-body p-4">
                    <div className="text-center mb-3">
                      <div
                        className={`icon icon-xl bg-${browser.color} bg-opacity-10 rounded-3 mx-auto mb-3`}
                      >
                        <i
                          className={`bi ${browser.icon} fs-1 text-${browser.color}`}
                        ></i>
                      </div>
                      <div className="d-flex justify-content-center align-items-center">
                        <h3 className="h4 mb-0">{browser.name}</h3>
                      </div>

                      {(browser.rating || browser.users) && (
                        <div className="d-flex justify-content-center align-items-center gap-3 mt-2">
                          {browser.rating && (
                            <span className="small">
                              <i className="bi bi-star-fill text-warning"></i>{" "}
                              {browser.rating}
                            </span>
                          )}
                          {browser.users && (
                            <span className="small text-muted">
                              {browser.users} users
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    <ul className="list-unstyled small mb-4">
                      {browser.features.map((feature, index) => (
                        <li key={index} className="mb-2">
                          <i className="bi bi-check text-success me-2"></i>
                          {feature}
                        </li>
                      ))}
                    </ul>

                    {browser.status === "available" ? (
                      <div className="d-grid">
                        <a
                          href={browser.downloadUrl}
                          className="btn btn-primary"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <i className="bi bi-download me-2"></i>
                          {t("extensions.browser.buttons.installNow")}
                        </a>
                      </div>
                    ) : (
                      <div className="d-grid mt-auto">
                        <span className="bg-warning-subtle text-warning px-3 py-2 rounded-3 d-flex align-items-center justify-content-center">
                          <i className="bi bi-clock me-1"></i>
                          {t("extensions.browser.buttons.comingQ3")}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-5 bg-light">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="display-6 fw-bold mb-4">
              {t("extensions.extensionFeatures.title")}
            </h2>
            <p className="lead text-muted">
              {t("extensions.extensionFeatures.subtitle")}
            </p>
          </div>

          <div className="row g-4">
            {extensionFeatures.map((feature, index) => (
              <div key={index} className="col-lg-4 col-md-6">
                <div className="d-flex align-items-start">
                  <div className="icon icon-lg bg-primary bg-opacity-10 rounded-3 me-3 flex-shrink-0">
                    <i className={`bi ${feature.icon} fs-4 text-primary`}></i>
                  </div>
                  <div>
                    <h4 className="h5 mb-2">{feature.title}</h4>
                    <p className="text-muted mb-0">{feature.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-5">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6 mb-4 mb-lg-0">
              <h2 className="display-6 fw-bold mb-4">
                {t("extensions.howItWorks.title")}
              </h2>
              <div className="d-flex flex-column gap-4">
                <div className="d-flex align-items-start">
                  <div
                    className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-3"
                    style={{ width: "40px", height: "40px", minWidth: "40px" }}
                  >
                    <span className="fw-bold">1</span>
                  </div>
                  <div>
                    <h4 className="h5 mb-2">
                      {t("extensions.howItWorks.steps.step1.title")}
                    </h4>
                    <p className="text-muted mb-0">
                      {t("extensions.howItWorks.steps.step1.description")}
                    </p>
                  </div>
                </div>

                <div className="d-flex align-items-start">
                  <div
                    className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-3"
                    style={{ width: "40px", height: "40px", minWidth: "40px" }}
                  >
                    <span className="fw-bold">2</span>
                  </div>
                  <div>
                    <h4 className="h5 mb-2">
                      {t("extensions.howItWorks.steps.step2.title")}
                    </h4>
                    <p className="text-muted mb-0">
                      {t("extensions.howItWorks.steps.step2.description")}
                    </p>
                  </div>
                </div>

                <div className="d-flex align-items-start">
                  <div
                    className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-3"
                    style={{ width: "40px", height: "40px", minWidth: "40px" }}
                  >
                    <span className="fw-bold">3</span>
                  </div>
                  <div>
                    <h4 className="h5 mb-2">
                      {t("extensions.howItWorks.steps.step3.title")}
                    </h4>
                    <p className="text-muted mb-0">
                      {t("extensions.howItWorks.steps.step3.description")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="bg-primary bg-opacity-10 rounded-4 p-4 d-flex justify-content-center">
                <ExtensionMock />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pocket Migration Callout */}
      <section className="py-4">
        <div className="container">
          <div className="row">
            <div className="col-lg-10 mx-auto">
              <div className="card border-0 bg-primary bg-opacity-10">
                <div className="card-body p-4 d-flex flex-column flex-md-row align-items-center justify-content-between gap-3">
                  <div className="d-flex align-items-center gap-3">
                    <div className="icon icon-lg bg-white rounded-3 shadow-sm">
                      <i className="bi bi-box-arrow-in-right fs-4 text-primary"></i>
                    </div>
                    <div>
                      <h4 className="h6 mb-1">Switching from Pocket?</h4>
                      <p className="text-muted mb-0 small">Import your Pocket bookmarks in seconds. Our extension makes the transition seamless.</p>
                    </div>
                  </div>
                  <Link to="/pocket-alternative" className="btn btn-primary btn-sm text-nowrap">
                    Learn About Migration
                    <i className="bi bi-arrow-right ms-2"></i>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technical Details */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="row">
            <div className="col-lg-10 mx-auto">
              <h3 className="h2 fw-bold mb-4 text-center">
                {t("extensions.technicalDetails.title")}
              </h3>

              <div className="row g-4">
                <div className="col-md-6">
                  <div className="card h-100 border-0 shadow-sm">
                    <div className="card-body p-4">
                      <h4 className="h5 mb-3">
                        <i className="bi bi-shield-check text-success me-2"></i>
                        {t("extensions.technicalDetails.privacy.title")}
                      </h4>
                      <ul className="list-unstyled mb-0">
                        {t("extensions.technicalDetails.privacy.features", {
                          returnObjects: true,
                        }).map((feature, index) => (
                          <li key={index} className="mb-2">
                            <i className="bi bi-check2 text-success me-2"></i>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="card h-100 border-0 shadow-sm">
                    <div className="card-body p-4">
                      <h4 className="h5 mb-3">
                        <i className="bi bi-cpu text-primary me-2"></i>
                        {t("extensions.technicalDetails.performance.title")}
                      </h4>
                      <ul className="list-unstyled mb-0">
                        {t("extensions.technicalDetails.performance.features", {
                          returnObjects: true,
                        }).map((feature, index) => (
                          <li key={index} className="mb-2">
                            <i className="bi bi-check2 text-success me-2"></i>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-5">
        <div className="container">
          <div className="banner-gradient rounded-4 p-5 text-center text-white">
            <h2 className="display-6 fw-bold mb-4 text-white">
              {t("extensions.cta.title")}
            </h2>
            <p className="lead mb-4">{t("extensions.cta.description")}</p>
            <div className="d-flex gap-3 justify-content-center flex-wrap">
              <a
                href="https://chrome.google.com/webstore/detail/onbookmarks"
                className="btn btn-light btn-lg px-5"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="bi bi-browser-chrome me-2"></i>
                {t("extensions.cta.buttons.getForChrome")}
              </a>
              <Link
                to={`${process.env.REACT_APP_APP_URL}/register`}
                className="btn btn-outline-light btn-lg px-4"
              >
                {t("extensions.cta.buttons.createAccount")}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Extensions;
